/**
 * Language Detection Module
 *
 * Automatically detects and sets the user's preferred language based on:
 * 1. Previously saved preference (localStorage)
 * 2. Geographic location (IP-based or timezone)
 * 3. Browser language settings
 *
 * Language Rules:
 * - Portuguese: Brazil, Portugal, Angola, Mozambique, Cape Verde, etc.
 * - Spanish: Spain, Mexico, Argentina, Colombia, and other Spanish-speaking countries
 * - English: Default for all other countries
 */

import logger from "./logger";

// Country to language mapping
const PORTUGUESE_COUNTRIES = [
  "BR", // Brazil
  "PT", // Portugal
  "AO", // Angola
  "MZ", // Mozambique
  "CV", // Cape Verde
  "GW", // Guinea-Bissau
  "ST", // São Tomé and Príncipe
  "TL", // East Timor
  "MO", // Macau
];

const SPANISH_COUNTRIES = [
  "ES", // Spain
  "MX", // Mexico
  "AR", // Argentina
  "CO", // Colombia
  "PE", // Peru
  "VE", // Venezuela
  "CL", // Chile
  "EC", // Ecuador
  "GT", // Guatemala
  "CU", // Cuba
  "BO", // Bolivia
  "DO", // Dominican Republic
  "HN", // Honduras
  "PY", // Paraguay
  "SV", // El Salvador
  "NI", // Nicaragua
  "CR", // Costa Rica
  "PA", // Panama
  "UY", // Uruguay
  "PR", // Puerto Rico
  "GQ", // Equatorial Guinea
];

/**
 * Detects the user's language based on multiple factors
 * Priority order:
 * 1. Saved language in localStorage
 * 2. Country-based detection
 * 3. Browser language preference
 * 4. Default to English
 */
export const detectUserLanguage = async (): Promise<string> => {
  // 1. Check if user has already saved a language preference
  const savedLanguage = localStorage.getItem("language");
  if (savedLanguage) {
    return savedLanguage;
  }

  try {
    // 2. Try to detect country using IP geolocation
    // Using ipapi.co free service (no API key required for basic usage)
    const geoResponse = await fetch("https://ipapi.co/json/", {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (geoResponse.ok) {
      const geoData = await geoResponse.json();
      const countryCode = geoData.country_code;

      if (countryCode) {
        // Check if country speaks Portuguese
        if (PORTUGUESE_COUNTRIES.includes(countryCode)) {
          return "pt";
        }

        // Check if country speaks Spanish
        if (SPANISH_COUNTRIES.includes(countryCode)) {
          return "es";
        }
      }
    }
  } catch (error: unknown) {
    logger.warn("Failed to detect location:", error);
  }

  // 3. Fallback to browser language preference
  const browserLang = navigator.language || navigator.languages?.[0];
  if (browserLang) {
    // Extract the primary language code (e.g., 'pt' from 'pt-BR')
    const primaryLang = browserLang.split("-")[0].toLowerCase();

    // Check if we support this language
    if (["pt", "es", "en"].includes(primaryLang)) {
      return primaryLang;
    }
  }

  // 4. Default to English
  return "en";
};

/**
 * Alternative method using browser's timezone to guess location
 * This is less accurate but doesn't require external API calls
 */
export const detectLanguageByTimezone = (): string => {
  const savedLanguage = localStorage.getItem("language");
  if (savedLanguage) {
    return savedLanguage;
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Map common timezones to languages
  const portugueseTimezones = [
    "America/Sao_Paulo",
    "America/Bahia",
    "America/Belem",
    "America/Boa_Vista",
    "America/Campo_Grande",
    "America/Cuiaba",
    "America/Fortaleza",
    "America/Maceio",
    "America/Manaus",
    "America/Noronha",
    "America/Porto_Velho",
    "America/Recife",
    "America/Rio_Branco",
    "America/Santarem",
    "Europe/Lisbon",
    "Africa/Luanda",
    "Africa/Maputo",
    "Atlantic/Cape_Verde",
    "Africa/Bissau",
    "Africa/Sao_Tome",
    "Asia/Dili",
    "Asia/Macau",
  ];

  const spanishTimezones = [
    "Europe/Madrid",
    "America/Mexico_City",
    "America/Argentina/Buenos_Aires",
    "America/Bogota",
    "America/Lima",
    "America/Caracas",
    "America/Santiago",
    "America/Guayaquil",
    "America/Guatemala",
    "America/Havana",
    "America/La_Paz",
    "America/Santo_Domingo",
    "America/Tegucigalpa",
    "America/Asuncion",
    "America/El_Salvador",
    "America/Managua",
    "America/Costa_Rica",
    "America/Panama",
    "America/Montevideo",
    "America/Puerto_Rico",
    "Africa/Malabo",
  ];

  if (portugueseTimezones.some((tz) => timezone.includes(tz))) {
    return "pt";
  }

  if (spanishTimezones.some((tz) => timezone.includes(tz))) {
    return "es";
  }

  // Check browser language as fallback
  const browserLang = navigator.language || navigator.languages?.[0];
  if (browserLang) {
    const primaryLang = browserLang.split("-")[0].toLowerCase();
    if (["pt", "es", "en"].includes(primaryLang)) {
      return primaryLang;
    }
  }

  return "en";
};
